import wrapperHOC from "@Utils/wrapperHOC";
import { memo } from "react";
import styles from "./ContentGridSection.module.scss";
import ContentCard from "@Components/ContentCard";
import type { ContentCardItem } from "@Components/ContentCard";
import CustomLink from "@Components/CustomLink";
import ChevronRightSmallIcon from "icons/ChevronRightSmallIcon";
import useIsDesktop from "hooks/useIsDesktop";
import Skeleton from "@Components/Skeleton";
import useTranslationHook from "hooks/useTranslationHook";

interface ContentGridSectionProps {
  items: ContentCardItem[];
  basePath: string;
  contentLabel: string;
  isLoading?: boolean;
  activeCategory?: string;
  activeCategorySlug?: string;
  mobileItemCount?: number;
  itemLinkTo?: (item: ContentCardItem) => string;
}

const toTitleCase = (str: string): string => str.replace(/\b\w/g, (char) => char.toUpperCase());

const ContentGridSection = ({
  items,
  basePath,
  contentLabel,
  isLoading = false,
  activeCategory = "Latest",
  activeCategorySlug = "",
  mobileItemCount = 6,
  itemLinkTo,
}: ContentGridSectionProps) => {
  const isDesktop = useIsDesktop();
  const { t } = useTranslationHook();
  const isNews = basePath === "/news";

  const displayLabel =
    isNews && activeCategory.toLowerCase() !== "latest" ? t("posts") : t(contentLabel);

  let viewAllLink: string;
  if (activeCategory.toLowerCase() === "latest") {
    viewAllLink = `${basePath}/latest`;
  } else if (activeCategorySlug) {
    viewAllLink = `${basePath}/category/${activeCategorySlug}`;
  } else {
    viewAllLink = basePath;
  }

  const titleText = `${t("See All")} ${t(activeCategory || "").toLowerCase()} ${t(displayLabel)}`;
  const viewAllText = toTitleCase(titleText);

  return (
    <div className={styles.gridSection}>
      <div className={styles.contentGrid}>
        {isLoading
          ? Array.from({ length: 12 }).map((_, index) => (
              <div key={index} className={styles.skeletonCard}>
                <Skeleton height="200px" />
                <div style={{ marginTop: "12px" }}>
                  <Skeleton height="20px" />
                </div>
                <div style={{ marginTop: "8px" }}>
                  <Skeleton height="16px" width="60%" />
                </div>
              </div>
            ))
          : items
              .slice(0, isDesktop ? items.length : mobileItemCount)
              .map((item) => (
                <ContentCard
                  key={item.id}
                  item={item}
                  basePath={basePath}
                  linkTo={itemLinkTo ? itemLinkTo(item) : undefined}
                />
              ))}
      </div>
      <CustomLink to={viewAllLink} redirect={isDesktop} className={styles.viewAllButton}>
        <span>{viewAllText}</span>
        <ChevronRightSmallIcon width={8} height={16} color="#374151" />
      </CustomLink>
    </div>
  );
};

export default wrapperHOC(memo(ContentGridSection), {
  componentName: "ContentGridSection",
  showForChina: true,
});
